/**
 * Master Dashboard — Phase 6 round 3.
 *
 * BU × Dept pivot of all new-flow Cost Centers, with a configurable date
 * range. For each (BU, Dept) intersection, renders the CCs at that
 * intersection with their planned total, range spend, used %, and SPOC.
 * Right column shows per-Dept totals (across BUs). Bottom row shows per-BU
 * totals (across Depts). Bottom-right shows the grand total.
 *
 * Layout: a real <table> with border-collapse so cells line up cleanly.
 * - The header row is sticky to the top of the scroll container.
 * - The first column (dept names) is sticky to the left so it stays visible
 *   while scrolling sideways through BUs.
 * - CC entries inside a cell stack vertically, separated by a hairline rule.
 *   Each CC's usage % is encoded both as a colored left-border on its row and
 *   a slim progress bar at the bottom.
 */

import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiFullscreen, mdiFullscreenExit } from "@mdi/js";
import { fetchGet } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import DashboardSection from "../components/dashboard_section";

// ---- types ----------------------------------------------------------------

interface CCRow {
  id: string;
  name: string;
  spoc_name: string;
  spoc_names: string[];
  total: number;
  spend: number;
  used_pct: number;
}

interface AxisRow {
  id: string;
  name: string;
  head_name: string;
  total: number;
  spend: number;
  used_pct: number;
}

interface MatrixData {
  range: { from: string; to: string; months: number };
  bus: AxisRow[];
  depts: AxisRow[];
  grid: Record<string, Record<string, CCRow[]>>;
  grand_total: { total: number; spend: number; used_pct: number };
}

// ---- helpers --------------------------------------------------------------

const formatShort = (n: number) => {
  const v = Math.abs(n);
  if (v >= 1e7) return "₹" + (n / 1e7).toFixed(2) + "Cr";
  if (v >= 1e5) return "₹" + (n / 1e5).toFixed(2) + "L";
  if (v >= 1e3) return "₹" + (n / 1e3).toFixed(1) + "K";
  return "₹" + Math.round(n);
};

const formatDateInput = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const formatDateLabel = (s: string) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Tailwind colors for a usage% bucket. Thresholds:
 *   0%       — empty (gray)
 *   0–75%    — healthy (Kazam teal / emerald)
 *   75–90%   — warning (amber)
 *   90–100%  — almost over (orange)
 *   >100%    — overrun (rose)
 */
const colorsFor = (pct: number) => {
  if (pct === 0)
    return { border: "border-l-gray-300", text: "text-gray-500", bar: "bg-gray-300" };
  if (pct < 75)
    return {
      border: "border-l-myPrimaryColor",
      text: "text-emerald-700",
      bar: "bg-myPrimaryColor",
    };
  if (pct < 90)
    return { border: "border-l-amber-500", text: "text-amber-700", bar: "bg-amber-500" };
  if (pct <= 100)
    return {
      border: "border-l-orange-500",
      text: "text-orange-700",
      bar: "bg-orange-500",
    };
  return { border: "border-l-rose-500", text: "text-rose-700", bar: "bg-rose-500" };
};

// ---- component ------------------------------------------------------------

const MatrixDashboardScreen: FunctionComponent = () => {
  const { showToast } = useToast();
  const navigation = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MatrixData | null>(null);

  // Single month picker (YYYY-MM). On change we compute from/to as the first
  // and last day of that month and pass them to the same backend endpoint.
  const today = useMemo(() => new Date(), []);
  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(currentMonthKey);
  const monthRange = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return {
      from: `${month}-01`,
      to: `${month}-${String(lastDay).padStart(2, "0")}`,
    };
  }, [month]);

  // True browser fullscreen via the Fullscreen API — removes the tab chrome
  // (URL bar, tabs) the same way Google Meet / Teams does. The CSS overlay
  // also applies as a backup in case the API call fails (some iframe contexts)
  // or while fullscreen mode is transitioning.
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    const el = fullscreenContainerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        // Vendor-prefixed fallback for older Safari (webkitRequestFullscreen).
        const req =
          el.requestFullscreen ||
          (el as any).webkitRequestFullscreen ||
          (el as any).msRequestFullscreen;
        if (req) await req.call(el);
      } else {
        const exit =
          document.exitFullscreen ||
          (document as any).webkitExitFullscreen ||
          (document as any).msExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch (_) {
      // Fall back to in-tab overlay if the browser blocks fullscreen (eg
      // sandboxed iframe). Toggle state manually so the CSS overlay kicks in.
      setIsFullscreen((v) => !v);
    }
  };

  // Sync our state with the browser. Triggered for both our toggle AND when the
  // user exits via Esc / F11 / the browser's exit button, so the icon updates.
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(
        !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).msFullscreenElement
        ),
      );
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/dashboard/matrix?from=${monthRange.from}&to=${monthRange.to}`;
      const res = await fetchGet(url);
      if (res.success) setData(res.data);
      else showToast({ type: ToastType.error, text: res.error });
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  // Auto-reload whenever the month changes (no Apply button needed).
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [month]);

  if (loading && !data) return <LoadingWidget />;
  if (!data) return null;

  // ---- render helpers ----------------------------------------------------

  /** A CC card rendered inside its <td>. Flush to the cell, no own shadow.
      Left accent bar removed — the progress bar + colored used% text already
      convey utilization, so the side bar was redundant. */
  const renderCC = (cc: CCRow, isLast: boolean) => {
    const c = colorsFor(cc.used_pct);
    return (
      <div
        key={cc.id}
        className={`px-2 py-1.5 ${
          isLast ? "" : "border-b border-gray-100"
        }`}
      >
        <button
          type="button"
          onClick={() => navigation.push(`${urls.cc_dashboard}/${cc.id}`)}
          title="Open CC dashboard"
          className="text-[11px] font-semibold text-gray-800 truncate leading-tight text-left w-full hover:text-myPrimaryColor hover:underline"
        >
          {cc.name}
        </button>
        <div className="mt-1 flex items-baseline justify-between gap-2 text-[10px]">
          <div className="flex gap-2 text-gray-500">
            <span>
              <span className="text-gray-400">T</span>{" "}
              <span className="font-medium text-gray-700 tabular-nums">
                {formatShort(cc.total)}
              </span>
            </span>
            <span>
              <span className="text-gray-400">S</span>{" "}
              <span className="font-medium text-gray-700 tabular-nums">
                {formatShort(cc.spend)}
              </span>
            </span>
          </div>
          <span className={`font-semibold tabular-nums ${c.text}`}>
            {cc.used_pct.toFixed(1)}%
          </span>
        </div>
        <div className="mt-1 h-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full ${c.bar}`}
            style={{ width: `${Math.min(cc.used_pct, 100)}%` }}
          />
        </div>
        {cc.spoc_name && (
          <div className="mt-0.5 text-[10px] text-gray-400 truncate">
            SPOC: {cc.spoc_name}
          </div>
        )}
      </div>
    );
  };

  /** Number cell content for a totals row/column. Left accent bar dropped to
      match the CC cards — progress bar + colored % text carry the utilization
      signal on their own. */
  const renderTotalContent = (row: { total: number; spend: number; used_pct: number }) => {
    const c = colorsFor(row.used_pct);
    return (
      <div className={`px-2 py-1.5`}>
        <div className="grid grid-cols-3 gap-1 text-[10px]">
          <div>
            <div className="text-gray-500">Total</div>
            <div className="font-medium tabular-nums text-gray-900">
              {formatShort(row.total)}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Spend</div>
            <div className="font-medium tabular-nums text-gray-900">
              {formatShort(row.spend)}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Used</div>
            <div className={`font-semibold tabular-nums ${c.text}`}>
              {row.used_pct.toFixed(1)}%
            </div>
          </div>
        </div>
        <div className="mt-1 h-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full ${c.bar}`}
            style={{ width: `${Math.min(row.used_pct, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  // Column widths locked via <colgroup> below. The classNames below intentionally
  // do NOT include width utilities — the table is `table-fixed` so widths are
  // taken from <colgroup>, not from each <td>. This guarantees every row's
  // column edges align perfectly with the header row's columns.
  // gray-300 borders are dark enough to remain visible against the gray-200
  // "Not applicable" cells without being so bold that they dominate normal cells.
  const cellBase = "border border-gray-300 align-top p-0";
  // Sticky-left first column. Need a background since the row underneath
  // can scroll past, and a higher z-index so it stays above body cells.
  const stickyFirstCol = `${cellBase} sticky left-0 z-10`;
  // Sticky-top header cells. The header background must be opaque so body
  // content doesn't bleed through as it scrolls underneath.
  const stickyHeader = `${cellBase} sticky top-0 z-20`;
  // The corner cell (row 0, col 0) is both sticky-top AND sticky-left — needs
  // the highest z so it stays above both the header row and the first column
  // when scrolling diagonally.
  const stickyCorner = `${cellBase} sticky top-0 left-0 z-30`;
  const FIRST_COL_W = 180;
  const BODY_COL_W = 220;

  return (
    <div className="p-3 sm:p-5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            Master Dashboard
          </h1>
          <p className="text-xs text-gray-500">
            BU × Department pivot &middot; {data.depts.length} departments ×{" "}
            {data.bus.length} business units &middot; {data.range.months} month
            {data.range.months === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-gray-500">
              Month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-gray-200 rounded-md px-2 py-1 text-xs"
            />
          </div>
          <button
            onClick={load}
            title="Reload current month"
            className="bg-myPrimaryColor text-white rounded-md text-xs px-3 py-1.5"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Grand total summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-md shadow-sm border-l-4 border-l-myPrimaryColor p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Grand Total Planned
          </div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900 mt-1">
            {formatShort(data.grand_total.total)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Sum of monthly estimate × {data.range.months}mo
          </div>
        </div>
        <div className="bg-white rounded-md shadow-sm border-l-4 border-l-blue-400 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Spend in Range
          </div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900 mt-1">
            {formatShort(data.grand_total.spend)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Finance-approved projects
          </div>
        </div>
        <div
          className={`bg-white rounded-md shadow-sm border-l-4 p-4 ${
            data.grand_total.used_pct > 100
              ? "border-l-rose-500"
              : data.grand_total.used_pct > 90
                ? "border-l-amber-500"
                : "border-l-emerald-500"
          }`}
        >
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Overall Utilization
          </div>
          <div className="text-xl md:text-2xl font-semibold text-gray-900 mt-1">
            {data.grand_total.used_pct.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {formatDateLabel(data.range.from)} — {formatDateLabel(data.range.to)}
          </div>
        </div>
      </div>

      {/* This ref is what gets passed to requestFullscreen(). When true OS-level
          fullscreen kicks in, this element fills the actual screen (no browser
          chrome). The bg-gray-100 fills the area cleanly during the transition
          and serves as a fallback when the Fullscreen API is unavailable. */}
      <div
        ref={fullscreenContainerRef}
        className={
          isFullscreen
            ? "bg-gray-100 overflow-auto p-3 sm:p-5 w-screen h-screen"
            : ""
        }
      >
      <DashboardSection
        title="BU × Department pivot"
        subtitle="Each cell lists the Cost Centers at the intersection. Scroll horizontally on smaller screens."
        actions={
          <button
            type="button"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen (Esc)" : "Open in fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Open in fullscreen"}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <Icon
              path={isFullscreen ? mdiFullscreenExit : mdiFullscreen}
              size={0.9}
            />
          </button>
        }
      >
        {/* max-h caps the vertical scroll region so the sticky thead has something
            to stick against; without it, the page itself scrolls and the header
            disappears off the top of the viewport. In fullscreen mode we extend
            this so the matrix takes most of the viewport. */}
        <div
          className="overflow-auto rounded-md border border-gray-300"
          style={{
            maxHeight: isFullscreen ? "calc(100vh - 180px)" : "75vh",
          }}
        >
          <table
            className="text-xs"
            style={{
              borderCollapse: "separate",
              borderSpacing: 0,
              tableLayout: "fixed",
              // Pre-compute table width: first col + body cols (BUs + 1 totals) × body width.
              width:
                FIRST_COL_W + (data.bus.length + 1) * BODY_COL_W + "px",
            }}
          >
            <colgroup>
              <col style={{ width: FIRST_COL_W + "px" }} />
              {data.bus.map((bu) => (
                <col key={bu.id} style={{ width: BODY_COL_W + "px" }} />
              ))}
              <col style={{ width: BODY_COL_W + "px" }} />
            </colgroup>
            <thead>
              <tr>
                {/* Corner cell — neutral; it's the intersection of both axes. */}
                <th className={`${stickyCorner} bg-gray-50 text-left p-2`}>
                  <div className="text-[11px] text-gray-500 italic font-normal leading-tight">
                    {formatDateLabel(data.range.from)} —{" "}
                    {formatDateLabel(data.range.to)}
                  </div>
                </th>
                {/* BU column headers — subtle sky/blue tint. Title is a link
                    to the BU dashboard. */}
                {data.bus.map((bu) => (
                  <th
                    key={bu.id}
                    className={`${stickyHeader} bg-sky-50 text-left p-2`}
                  >
                    <button
                      type="button"
                      onClick={() => navigation.push(`${urls.bu_dashboard}/${bu.id}`)}
                      title="Open BU dashboard"
                      className="text-xs font-semibold text-sky-900 truncate leading-tight text-left w-full hover:underline"
                    >
                      BU &mdash; {bu.name}
                    </button>
                    <div className="text-[10px] text-sky-700/80 truncate mt-0.5 font-normal">
                      {bu.head_name ? `BU Head(s): ${bu.head_name}` : "BU Head(s): —"}
                    </div>
                  </th>
                ))}
                {/* Dept(s) Total header — bold dark-purple banner with white
                    text. Visually "calls out" the totals column. */}
                <th className={`${stickyHeader} bg-purple-700 text-left p-2`}>
                  <div className="text-xs font-semibold text-white truncate leading-tight">
                    Dept.(s) Total
                  </div>
                  <div className="text-[10px] text-purple-100 mt-0.5 font-normal">
                    Across all BUs
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.depts.map((dp) => (
                <tr key={dp.id}>
                  {/* Dept row header — subtle purple/lavender tint. Distinct
                      from the sky-blue BU column headers and free of the
                      red/green accounting connotations. Title links to the
                      Dept dashboard. */}
                  <td className={`${stickyFirstCol} bg-purple-50 p-2`}>
                    <button
                      type="button"
                      onClick={() => navigation.push(`${urls.dept_dashboard}/${dp.id}`)}
                      title="Open Department dashboard"
                      className="text-xs font-semibold text-purple-900 truncate leading-tight text-left w-full hover:underline"
                    >
                      Dept &mdash; {dp.name}
                    </button>
                    <div className="text-[10px] text-purple-700/80 truncate mt-0.5">
                      {dp.head_name ? `Dept. Head(s): ${dp.head_name}` : "Dept. Head(s): —"}
                    </div>
                  </td>
                  {data.bus.map((bu) => {
                    const ccs = data.grid[bu.id]?.[dp.id] || [];
                    const isEmpty = ccs.length === 0;
                    return (
                      <td
                        key={bu.id + "::" + dp.id}
                        className={`${cellBase} ${isEmpty ? "bg-gray-200" : "bg-white"}`}
                      >
                        {isEmpty ? (
                          <div className="text-[11px] text-black italic text-center py-6">
                            Not applicable
                          </div>
                        ) : (
                          <div>
                            {ccs.map((cc, i) =>
                              renderCC(cc, i === ccs.length - 1),
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {/* Dept totals column — opaque white so sticky-first-col
                      from rows below doesn't bleed through during scroll. */}
                  <td className={`${cellBase} bg-white`}>
                    {renderTotalContent(dp)}
                  </td>
                </tr>
              ))}
              {/* BU(s) Total row — first cell is the dark-cyan banner with
                  white text. Body cells stay white so the totals data remains
                  legible. */}
              <tr>
                <td
                  className={`${stickyFirstCol} bg-cyan-700 p-2`}
                  style={{ borderTop: "2px solid #a5f3fc" }}
                >
                  <div className="text-xs font-semibold text-white">
                    BU(s) Total
                  </div>
                  <div className="text-[10px] text-cyan-100 mt-0.5">
                    Across all Depts
                  </div>
                </td>
                {data.bus.map((bu) => (
                  <td
                    key={bu.id}
                    className={`${cellBase} bg-white`}
                    style={{ borderTop: "2px solid #a5f3fc" }}
                  >
                    {renderTotalContent(bu)}
                  </td>
                ))}
                <td
                  className={`${cellBase} bg-emerald-50`}
                  style={{ borderTop: "2px solid #a5f3fc" }}
                >
                  {renderTotalContent(data.grand_total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-myPrimaryColor" />
            0–75% — healthy
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
            75–90% — warning
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
            90–100% — almost over
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
            &gt;100% — overrun
          </span>
          <span className="ml-2">
            <span className="text-gray-400">T</span> = Total &middot;{" "}
            <span className="text-gray-400">S</span> = Spend
          </span>
        </div>
      </DashboardSection>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default MatrixDashboardScreen;
